import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { createFolder as createRecord } from "../../../redux/actions/folderActions";
import { useStepNavigation } from "../../../hooks/useStepNavigation";
import { useCreateRecordForm } from "../../../hooks/useCreateRecordForm";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription } from "../../ui/alert";
import { StepIndicator } from "../../ui/step-indicator";
import { Step1Inquilino } from "./Step1Inquilino";
import { Step2Asesor } from "./Step2Asesor";
import { Step3Fiador } from "./Step3Fiador";
import { Step4ConvenioRepresentante } from "./Step4ConvenioRepresentante";
import { Step5Review } from "./Step5Review";
import { isStep1Valid, isStep2Valid, isStep3Valid, isStep4Valid } from "./validation";
import { STEPS } from "./constants";
import { extractErrorMessage } from "../../../utils/extractErrorMessage.ts";
import {
  isPreviousFolioError,
  PREVIOUS_FOLIO_STEP,
} from "../../../utils/folders/createRecordError.ts";

interface CreateRecordModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateRecordModal: React.FC<CreateRecordModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.folder);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formData,
    errors,
    updateFormData,
    resetForm,
    validateField,
    selectedTemplates,
    submitError,
    reportSubmitError,
    clearSubmitError,
  } = useCreateRecordForm();

  const {
    currentStep,
    isAnimating,
    handleNext: originalHandleNext,
    handleBack: originalHandleBack,
    handleStepClick: originalHandleStepClick,
    getStepProgress,
    resetStep,
  } = useStepNavigation(5);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Step configuration
  const steps = STEPS.map((step) => ({
    ...step,
    completed: currentStep > step.number,
  }));

  // Scroll to top helper
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  // Scroll to bottom when indicator is clicked
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Wrap navigation functions to include scroll to top
  const handleNext = () => {
    originalHandleNext();
    setTimeout(scrollToTop, 50);
  };

  const handleBack = () => {
    originalHandleBack();
    setTimeout(scrollToTop, 50);
  };

  const handleStepClick = (stepNumber: number) => {
    originalHandleStepClick(stepNumber);
    setTimeout(scrollToTop, 50);
  };

  // MB-379: "Corregir folio anterior" disappears with step 5, so focus would fall back to
  // the dialog. Once step 1 is on screen, the folio field gets it (and its error is read).
  const focusPreviousFolioRef = useRef(false);

  const goFixPreviousFolio = () => {
    focusPreviousFolioRef.current = true;
    handleStepClick(PREVIOUS_FOLIO_STEP);
  };

  useEffect(() => {
    if (
      !focusPreviousFolioRef.current ||
      currentStep !== PREVIOUS_FOLIO_STEP ||
      isAnimating
    ) {
      return;
    }
    focusPreviousFolioRef.current = false;
    document.getElementById("previousFolio")?.focus();
  }, [currentStep, isAnimating]);

  // MB-379: while the request runs the submit button is disabled, so a failure leaves focus on
  // <body>. The error box takes it: keyboard users land on the reason, not behind the overlay.
  const submitErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitError && currentStep === 5) submitErrorRef.current?.focus();
  }, [submitError, currentStep]);

  // Check if there's content to scroll and update indicator visibility
  const checkScrollable = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const hasScrollableContent = scrollHeight > clientHeight;
      // More precise check - consider it at bottom if within 5px of the end
      const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 5;
      setShowScrollIndicator(hasScrollableContent && isNotAtBottom);
    }
  };

  // Set up scroll listener and MutationObserver whenever the modal content mounts
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      checkScrollable();
    };

    const handleResize = () => {
      checkScrollable();
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    // Set up MutationObserver to detect content changes
    const observer = new MutationObserver(() => {
      checkScrollable();
    });

    observer.observe(scrollContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    // Check initially after a small delay to ensure content is rendered
    const initialCheckTimeout = window.setTimeout(checkScrollable, 100);

    // Also check when window resizes
    window.addEventListener("resize", handleResize);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      window.clearTimeout(initialCheckTimeout);
    };
  }, [isOpen]);

  // Re-check scrollable content when step changes or modal opens
  useEffect(() => {
    setTimeout(checkScrollable, 100);
    // Also check after animations complete
    setTimeout(checkScrollable, 500);
  }, [currentStep, isOpen]);

  // Check when form data changes
  useEffect(() => {
    checkScrollable();
    setTimeout(checkScrollable, 100);
  }, [
    formData.requiresGuarantor,
    formData.alternativeJustice,
  ]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      clearSubmitError();

      // Build the payload following the OnCreateFolderRequest interface
      const payload: any = {
        tenant_name: formData.tenantName,
        tenant_first_surname: formData.tenantFirstSurname,
        tenant_email: formData.tenantEmail.toLowerCase(),
        tenant_phone_number: formData.tenantPhoneNumber,
        advisor_name: formData.advisorName,
        advisor_first_surname: formData.advisorFirstSurname,
        advisor_email: formData.advisorEmail.toLowerCase(),
        advisor_phone_number: formData.advisorPhoneNumber,
        is_renewal: formData.isRenewal,
        templates: selectedTemplates,
      };

      // Add optional tenant fields
      if (formData.tenantSecondSurname) {
        payload.tenant_second_surname = formData.tenantSecondSurname;
      }
      if (formData.tenantCurp) {
        payload.tenant_curp = formData.tenantCurp;
      }
      if (formData.tenantRfc) {
        payload.tenant_rfc = formData.tenantRfc;
      }

      // Add optional advisor fields
      if (formData.advisorSecondSurname) {
        payload.advisor_second_surname = formData.advisorSecondSurname;
      }

      // Add guarantor email if provided
      if (formData.requiresGuarantor && formData.guarantorEmail) {
        payload.guarantor_email = formData.guarantorEmail.toLowerCase();
      }

      // Add landlord email if provided
      if (formData.alternativeJustice && formData.landlordEmail) {
        payload.landlord_email = formData.landlordEmail.toLowerCase();
      }

      // MB-362: previous folio, only when the expediente is a renewal
      if (formData.isRenewal && formData.previousFolio) {
        payload.previous_folio = formData.previousFolio;
      }

      // Create the record
      const response = await dispatch(createRecord(payload)).unwrap();

      // Root `Toaster` (AppRouter): it sits above any overlay and survives this modal's
      // page unmounting when we navigate to the new folder.
      toast.success("Expediente creado exitosamente.");

      // Close modal and reset state
      onOpenChange(false);
      resetStep();
      resetForm();
      setIsSubmitting(false);

      // Navigate to the record detail page
      if (response?.id) {
        navigate(`/folders/${response.id}`);
      }
    } catch (error: any) {
      setIsSubmitting(false);
      // MB-379: inline, not a toast — the toast rendered behind the dialog overlay and the
      // user saw nothing. The form is kept so they can fix it.
      reportSubmitError(
        extractErrorMessage(error, "Error al crear el expediente."),
      );
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // Reset form when closing
      resetStep();
      resetForm();
    }
    onOpenChange(open);
  };

  // Determine if current step is valid
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return isStep1Valid(formData);
      case 2:
        return isStep2Valid(formData);
      case 3:
        return isStep3Valid(formData);
      case 4:
        return isStep4Valid(formData);
      case 5:
        return true; // Review step is always valid
      default:
        return false;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange} modal={true}>
        <DialogContent
          className="w-full max-w-[calc(100vw-2rem)] sm:max-w-4xl sm:min-w-[56rem] max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-none sm:rounded-lg"
          showCloseButton={true}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="pb-4 shrink-0">
            <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900">
              Crear Expediente
            </DialogTitle>
            <StepIndicator
              steps={steps}
              currentStep={currentStep}
              onStepClick={handleStepClick}
              getStepProgress={getStepProgress}
            />
          </DialogHeader>

          {/* The pill is anchored to the scroll area, not to the dialog: as `fixed` inside the
              transformed dialog it sat a fixed distance above the footer and covered whatever
              appears in between (the submit error and its "Corregir folio anterior" button). */}
          <div className="relative flex-1 min-h-0 flex flex-col">
            {/* Floating scroll indicator pill */}
            {showScrollIndicator && (
              <div className="absolute bottom-3 right-4 z-10">
                <button
                  onClick={scrollToBottom}
                  className="bg-gray-500/80 backdrop-blur text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-1 hover:bg-gray-600/80 transition-colors"
                >
                  <span className="text-xs font-medium">Ver más</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}

            <div
              ref={scrollContainerRef}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-1"
            >
              <div className="min-h-[400px] pb-4">
                {currentStep === 1 && (
                  <Step1Inquilino
                    formData={formData}
                    errors={errors}
                    updateFormData={updateFormData}
                    validateField={validateField}
                    isAnimating={isAnimating}
                  />
                )}
                {currentStep === 2 && (
                  <Step2Asesor
                    formData={formData}
                    errors={errors}
                    updateFormData={updateFormData}
                    validateField={validateField}
                    isAnimating={isAnimating}
                  />
                )}
                {currentStep === 3 && (
                  <Step3Fiador
                    formData={formData}
                    errors={errors}
                    updateFormData={updateFormData}
                    validateField={validateField}
                    isAnimating={isAnimating}
                  />
                )}
                {currentStep === 4 && (
                  <Step4ConvenioRepresentante
                    formData={formData}
                    errors={errors}
                    updateFormData={updateFormData}
                    validateField={validateField}
                    isAnimating={isAnimating}
                  />
                )}
                {currentStep === 5 && (
                  <Step5Review
                    formData={formData}
                    selectedTemplates={selectedTemplates}
                    isAnimating={isAnimating}
                  />
                )}
              </div>
            </div>
          </div>

          {currentStep === 5 && submitError && (
            <Alert
              ref={submitErrorRef}
              tabIndex={-1}
              variant="destructive"
              className="shrink-0 bg-red-50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="size-4 shrink-0 mt-0.5" />
                <AlertDescription>
                  <p className="font-medium">No se creó el expediente</p>
                  <p>{submitError}</p>
                </AlertDescription>
              </div>
              {isPreviousFolioError(submitError) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goFixPreviousFolio}
                  className="shrink-0"
                >
                  Corregir folio anterior
                </Button>
              )}
            </Alert>
          )}

          <DialogFooter className="flex-row items-center justify-between pt-4 border-t border-gray-200 shrink-0 gap-2 sm:justify-between">
            {currentStep === 1 ? (
              <Button
                variant="ghost"
                onClick={() => handleDialogOpenChange(false)}
                className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                Regresar
              </Button>
            )}
            <div className="flex items-center gap-2 sm:gap-4">
              <Badge
                variant="secondary"
                className="px-2 py-1 bg-gray-100 text-gray-700 text-[11px] sm:px-2.5 sm:text-xs"
              >
                <span className="sm:hidden">{currentStep}/5</span>
                <span className="hidden sm:inline">
                  Paso {currentStep} de 5
                </span>
              </Badge>
              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  disabled={!isCurrentStepValid()}
                  className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || loading}
                  className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting || loading
                    ? "Creando..."
                    : "Crear Expediente"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
